import React, { useState, useEffect } from "react";
import moment from "moment";
import { Creatable } from "react-select";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCompressArrowsAlt, faExpandArrowsAlt } from "@fortawesome/free-solid-svg-icons";
import { Button } from "aible";
import { useDispatch, useSelector } from "types/store";
import { isEnabled } from "utils/featureFlags";
import { applyCorpusChunkSizeOptions, deleteCorpus, getCorpus, getCorpusDocumentList, getCorpusParameters, uploadDocumentUrls, updateArtifactAndSave, checkAllowCorpusModelSelect } from "containers/LanguageModelArtifact/reducer";
import { handleSelectUseCase, getDefaultEmbeddingsModelOptions, getDefaultEmbeddingsModel } from "containers/ChatGPT/reducer";
import { getAccountName, getSelectedAccountId } from "containers/Accounts/reducer";
import { updateAgentAutonomyView } from "containers/View/reducer";
import Tooltip from "components/Inputs/Tooltip";
import { LineInput } from "components/Inputs/LineInput";
import SenseCorpusErrorCard from "components/Tiles/Sense/SenseCorpusErrorCard";
import LoadingIndicator from "components/LoadingIndicator/LoadingIndicator";
import UploadDocument from "components/LanguageModelArtifact/UploadDocument";
import documentSetIcon from "images/files-light.svg";
import documentSetIconGray from "images/files-gray.svg";
import PinnedIcon from "images/Sense-Icons/pinned-white.svg";
import UnpinnedIcon from "images/SenseIcons/thumbtack-regular.svg";
import PinnedHoverIcon from "images/Sense-Icons/unpin-white.svg";
import GearIcon from "images/SenseIcons/cog-light 2.svg";
import ClockIcon from "images/SenseIcons/clock-rotate-left-light 1.svg";
import TrashIcon from "images/SenseIcons/trash-regular.svg";
import styles from "./style.module.css";
import * as foldersReducer from "containers/Folders/reducer";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/aible/Dialog/Dialog";
const DEFAULT_PARAMETERS = {
    chunk_size: 4000,
    text_splitter_type: "token",
    chunk_overlap: 0,
    pdf_parse_type: "standard"
};
const CorpusSettings = ({ corpusId, isOpenSettings, isOpenDocuments, toggleIsOpen, isLoadingCorpus, updateIsLoadingCorpus }) => {
    const dispatch = useDispatch();
    const accountId = useSelector((state) => getSelectedAccountId(state));
    const model = useSelector((state) => getDefaultEmbeddingsModel(state, accountId));
    const corpus = useSelector((state) => getCorpus(state, corpusId));
    const defaultParameters = {
        ...DEFAULT_PARAMETERS,
        model: model
    };
    const currentParameters = useSelector((state) => getCorpusParameters(state, corpusId)) ?? defaultParameters;
    const modelOptions = useSelector((state) => getDefaultEmbeddingsModelOptions(state));
    const [parameters, updateParameters] = useState(currentParameters);
    const [artifactTitle, updateArtifactTitle] = useState(corpus?.name);
    useEffect(() => {
        updateParameters(currentParameters);
    }, [corpusId, corpus?.metadata.status]);
    const parametersModified = currentParameters.text_splitter_type != parameters.text_splitter_type ||
        currentParameters.chunk_size != parameters.chunk_size ||
        currentParameters.chunk_overlap != parameters.chunk_overlap ||
        currentParameters.pdf_parse_type != parameters.pdf_parse_type ||
        currentParameters.processor_type != parameters.processor_type;
    const createdBy = useSelector((state) => getCorpus(state, corpusId)?.created_by);
    const handleApplyChunkSize = (params) => {
        updateIsLoadingCorpus(true);
        dispatch(applyCorpusChunkSizeOptions(corpusId, params))
            .then((c) => {
            if (c && c.metadata.parameters) {
                updateParameters(c.metadata.parameters);
            }
        })
            .catch((e) => {
            updateIsLoadingCorpus(false);
            console.log(e);
            throw e;
        })
            .finally(() => updateIsLoadingCorpus(false));
    };
    const allowCorpusModelSelect = checkAllowCorpusModelSelect(parameters.processor_type);
    const updateProcessorType = (type) => {
        updateParameters({
            ...parameters,
            processor_type: type
        });
    };
    const updateChunkSize = (chunk_size) => updateParameters({
        ...parameters,
        chunk_size: Number(chunk_size)
    });
    const updateChunkOverlap = (chunk_overlap) => updateParameters({
        ...parameters,
        chunk_overlap: Number(chunk_overlap)
    });
    const updatePDFParseType = (pdf_parse_type) => updateParameters({ ...parameters, pdf_parse_type });
    const updateModelId = (modelId) => updateParameters({ ...parameters, model: modelId });
    const corpusModel = modelOptions.find((o) => o.value === parameters.model)?.value ??
        parameters.model;
    const handleUpdateName = (newName) => {
        if (corpus) {
            dispatch(updateArtifactAndSave(corpus, { name: newName }));
        }
    };
    const selectedFolder = useSelector((state) => foldersReducer.getSelectedFolder(state));
    const canEdit = useSelector((state) => foldersReducer.getUserEditPermission(state, selectedFolder));
    return (React.createElement("div", { className: isOpenSettings ? styles.openOverview : styles.overview, style: {
            maxHeight: isOpenSettings ? "5000px" : "190px",
            width: isOpenSettings || isOpenDocuments ? "100%" : "calc(33% - 10px)",
            overflow: isOpenSettings ? "auto" : "hidden",
            marginLeft: isOpenSettings || isOpenDocuments ? "10px" : "0px"
        }, onClick: !isOpenSettings ? toggleIsOpen : undefined },
        React.createElement("div", { className: styles.senseTitle },
            React.createElement("div", { className: styles.sTitle },
                React.createElement("span", { className: styles.sIcon },
                    React.createElement("img", { src: GearIcon, style: { height: "16px", width: "16px" } })),
                React.createElement("span", null, "Settings")),
            isOpenSettings ? (React.createElement("div", { className: styles.closeButton, onClick: () => toggleIsOpen() },
                React.createElement(Button, { style: { padding: "3px" } },
                    React.createElement("div", { className: styles.selIcon, title: "Close" },
                        React.createElement(FontAwesomeIcon, { icon: faCompressArrowsAlt }))))) : (React.createElement("div", { className: styles.selIcon + " " + styles.expandIcon },
                React.createElement(FontAwesomeIcon, { icon: faExpandArrowsAlt })))),
        createdBy && (React.createElement("div", { className: styles.tileFlex },
            React.createElement("div", { className: styles.tTop }, createdBy?.fullname),
            React.createElement("div", { className: styles.tBot }, "CREATED BY"))),
        isOpenSettings && (React.createElement("div", null,
            React.createElement("div", { style: { marginBottom: "20px" } },
                React.createElement("div", null,
                    React.createElement(LineInput, { isSenseBox: true, value: artifactTitle, onChange: (val) => updateArtifactTitle(val), updateInstant: true, isDisabled: !canEdit })),
                isLoadingCorpus ? (React.createElement(LoadingIndicator, null)) : (canEdit && (React.createElement("div", { className: styles.settingsButtons },
                    React.createElement(Button, { onClick: () => updateArtifactTitle(corpus?.name) }, "Cancel"),
                    React.createElement(Button, { type: "primary", onClick: () => {
                            handleUpdateName(artifactTitle);
                        }, disabled: !artifactTitle }, "Apply"))))),
            isEnabled("document-set-processor-type") && (React.createElement(CorpusProcessorType, { isLoading: isLoadingCorpus, processorType: parameters.processor_type, updateProcessorType: updateProcessorType, corpusId: corpusId })),
            allowCorpusModelSelect && parameters.model ? (React.createElement("div", { className: styles.vectorSetting },
                React.createElement("div", { className: styles.vectorInfoButton },
                    React.createElement("div", null, "Embedding Model")),
                React.createElement(Creatable, { options: modelOptions, value: modelOptions.find((o) => o.value === corpusModel) ?? {
                        label: corpusModel,
                        value: corpusModel
                    }, onChange: (val) => {
                        const retVal = val;
                        updateModelId(retVal?.value);
                    }, placeholder: "Select a Model", menuPlacement: "bottom" }))) : null,
            React.createElement(React.Fragment, null,
                React.createElement("div", { className: styles.vectorSetting },
                    React.createElement("div", { className: styles.vectorInfoButton },
                        React.createElement("div", null, "Excerpt Size"),
                        React.createElement(Tooltip, { overlay: "This sets the maximum number of tokens each excerpt can contain. Setting this to a smaller size may allow you to add more total excerpts." })),
                    React.createElement(LineInput, { value: parameters.chunk_size, onChange: (val) => updateChunkSize(val), isDisabled: isLoadingCorpus, updateInstant: true })),
                React.createElement("div", { className: styles.vectorSetting },
                    React.createElement("div", { className: styles.vectorInfoButton },
                        React.createElement("div", null, "Excerpt Overlap"),
                        React.createElement(Tooltip, { overlay: "This sets the maximum number of tokens that can overlap (match) in adjacent excerpts." })),
                    React.createElement(LineInput, { value: parameters.chunk_overlap, onChange: (val) => updateChunkOverlap(val), isDisabled: isLoadingCorpus, updateInstant: true })),
                React.createElement("div", { className: styles.vectorSetting },
                    React.createElement("div", { className: styles.vectorInfoButton },
                        React.createElement("div", { className: styles.vectorInfoText }, "PDF Parse Mode"),
                        React.createElement(Tooltip, { overlay: "This controls whether or not the parser will attempt to detect and extract tabular data from PDFs. Enabling table parsing will lead to lower quality results for non tabular data and longer processing times." })),
                    React.createElement("div", { className: styles.vectorOptionsRadioContainer },
                        React.createElement("div", { className: styles.vectorOptionsRadioButton, onClick: () => {
                                if (!isLoadingCorpus) {
                                    updatePDFParseType("standard");
                                }
                            } },
                            React.createElement("input", { type: "radio", name: "pdfParseType", value: "standard", checked: parameters.pdf_parse_type === "standard" || parameters.pdf_parse_type === undefined, disabled: isLoadingCorpus }),
                            React.createElement("span", { className: styles.vectorOptionsRadioLabel }, "Standard (Faster & More Accurate)")),
                        React.createElement("div", { className: styles.vectorOptionsRadioButton, onClick: () => {
                                if (!isLoadingCorpus) {
                                    updatePDFParseType("table");
                                }
                            } },
                            React.createElement("input", { type: "radio", name: "pdfParseType", value: "table", checked: parameters.pdf_parse_type === "table", disabled: isLoadingCorpus }),
                            React.createElement("span", { className: styles.vectorOptionsRadioLabel }, "Optimized For Tables (Less Accurate)"))))),
            (parametersModified || isLoadingCorpus) && (React.createElement("div", { className: styles.infoWarningWrapper },
                React.createElement("div", { className: styles.infoWarningTitle }, isLoadingCorpus ? "Re-index in progress..." : "Almost done!"),
                React.createElement("div", null, isLoadingCorpus
                    ? "Please wait a moment. This should only take a few seconds."
                    : "Click Apply to re-index the content based on your new settings. This should only take a few seconds."),
                React.createElement("div", { className: styles.infoWarningButtonWrapper }, isLoadingCorpus ? (React.createElement(LoadingIndicator, { style: {
                        width: "40px",
                        height: "18px",
                        transform: "scale(0.40)",
                        margin: "-10px 5px 10px -10px"
                    } })) : (React.createElement(React.Fragment, null,
                    React.createElement(Button, { isSmall: true, type: "secondary", style: {
                            marginRight: "18px",
                            padding: "5px 12px"
                        }, onClick: () => {
                            updateParameters(currentParameters);
                        } }, "Cancel"),
                    React.createElement(Button, { isSmall: true, type: "secondary", onClick: () => {
                            return handleApplyChunkSize(parameters);
                        }, style: {
                            padding: "5px 12px"
                        } }, "Apply"))))))))));
};
export const CorpusProcessorType = ({ isLoading, processorType, updateProcessorType, corpusId }) => {
    return (React.createElement("div", { className: styles.vectorSetting },
        React.createElement("div", { className: styles.vectorInfoButton },
            React.createElement("div", { className: styles.vectorInfoText }, "Document Processor Type")),
        React.createElement("div", { className: styles.vectorOptionsRadioContainer },
            isEnabled("nv-ingest-document-processor-type") && (React.createElement("div", { className: styles.vectorOptionsRadioButton, onClick: () => {
                    if (!isLoading) {
                        updateProcessorType("nvingest");
                    }
                } },
                React.createElement("input", { type: "radio", name: `${corpusId}-processorType`, value: "nvingest", checked: processorType === "nvingest", disabled: isLoading }),
                React.createElement("span", { className: styles.vectorOptionsRadioLabel }, "NVIngest (better for documents with images and tables)"))),
            React.createElement("div", { className: styles.vectorOptionsRadioButton, onClick: () => {
                    if (!isLoading) {
                        updateProcessorType("default");
                    }
                } },
                React.createElement("input", { type: "radio", name: `${corpusId}-processorType`, value: "default", checked: processorType === "default" || processorType === undefined, disabled: isLoading }),
                React.createElement("span", { className: styles.vectorOptionsRadioLabel }, "Standard")))));
};
const CorpusAction = ({ corpusId, openCorpusUrlModal, isLoadingCorpus, updateIsLoadingCorpus, isNotEmptyDataset }) => {
    const dispatch = useDispatch();
    if (isLoadingCorpus) {
        return (React.createElement("div", { className: styles.openOverview, style: {
                maxHeight: "500px",
                width: "100%",
                overflow: "show",
                marginLeft: "10px"
            } },
            React.createElement("div", { className: styles.senseActionsBody },
                React.createElement("div", { className: styles.corpusLoadingMessage },
                    React.createElement(LoadingIndicator, { style: {
                            width: "40px",
                            height: "18px",
                            transform: "scale(0.40)",
                            margin: "-10px 5px 10px -10px"
                        } }),
                    React.createElement("span", { style: { marginLeft: "10px" } }, "Your document is processing. Please do not navigate away or refresh the page until the spinner completes.")))));
    }
    return (React.createElement("div", { className: styles.openOverview, style: {
            maxHeight: "500px",
            width: "100%",
            overflow: "show",
            marginLeft: "10px"
        } },
        React.createElement("div", { className: styles.senseActionsBody },
            React.createElement("span", null, "What would you like to do?")),
        React.createElement("div", { className: styles.dButtons },
            React.createElement(UploadDocument, { artifactId: corpusId, isSmall: false, style: {
                    minWidth: "90px",
                    minHeight: "40px",
                    padding: "9px 14px",
                    marginRight: "10px"
                }, onStart: () => {
                    updateIsLoadingCorpus(true);
                }, onComplete: () => {
                    updateIsLoadingCorpus(false);
                } }),
            isEnabled("chat-aible") && (React.createElement(React.Fragment, null,
                isEnabled("chat-aible-document-summarization-url") && (React.createElement(Button, { onClick: () => openCorpusUrlModal(), style: {
                        minWidth: "90px",
                        minHeight: "40px",
                        padding: "9px 14px",
                        marginRight: "10px"
                    } }, "Add URLs")),
                isNotEmptyDataset && (React.createElement(Button, { onClick: () => {
                        if (isEnabled("agent-autonomy-controls")) {
                            dispatch(updateAgentAutonomyView("RECOMMENDED"));
                        }
                        dispatch(handleSelectUseCase("CHATAIBLE_DOCUMENT_SUMMARIZATION", corpusId));
                    }, style: {
                        minWidth: "90px",
                        minHeight: "40px",
                        padding: "9px 14px",
                        marginRight: "10px"
                    } }, "Document Summarization Chat"))))),
        React.createElement("div", { className: styles.actionsTip },
            React.createElement("div", { className: styles.actionsTipText }, "SUPPORTED DOCUMENTS"),
            React.createElement(Tooltip, { overlay: React.createElement("div", { className: styles.actionsTipList },
                    React.createElement("div", null, "PDF"),
                    React.createElement("div", null, "CSV"),
                    React.createElement("div", null, "HTML"),
                    React.createElement("div", null, "MD")) }))));
};
const CorpusDocumentList = ({ corpusId, isOpenSettings, isOpenDocuments, updateIsOpenDocuments, isLoadingCorpus }) => {
    const documentList = useSelector((state) => getCorpusDocumentList(state, corpusId));
    const filteredDocs = documentList?.filter((doc) => {
        if (doc.startsWith("embedding") && doc.endsWith(".json")) {
            return false;
        }
        return true;
    });
    const hasDocuments = filteredDocs?.length > 0;
    return (React.createElement("div", { className: hasDocuments ? styles.overview : styles.openOverview, style: {
            maxHeight: "300px",
            width: isOpenSettings || isOpenDocuments ? "100%" : "calc(33.5% - 15px)",
            overflow: "hidden",
            marginLeft: "10px"
        }, onClick: !isOpenDocuments && hasDocuments ? () => updateIsOpenDocuments(!isOpenDocuments) : undefined },
        React.createElement("div", { className: styles.senseTitle },
            React.createElement("div", { className: styles.sTitle },
                React.createElement("span", { className: styles.sIcon },
                    React.createElement("img", { src: documentSetIconGray, style: { height: "16px", width: "16px" } })),
                React.createElement("span", null, "Documents")),
            isOpenDocuments ? (React.createElement("div", { className: styles.closeButton, onClick: () => updateIsOpenDocuments(!isOpenDocuments) },
                React.createElement(Button, { style: { padding: "3px" } },
                    React.createElement("div", { className: styles.selIcon, title: "Close" },
                        React.createElement(FontAwesomeIcon, { icon: faCompressArrowsAlt }))))) : (React.createElement("div", { className: styles.selIcon + " " + styles.expandIcon },
                React.createElement(FontAwesomeIcon, { icon: faExpandArrowsAlt })))),
        React.createElement("div", { className: styles.tileFlex },
            React.createElement("div", { className: styles.tTop }, !isLoadingCorpus ? (React.createElement("div", null,
                React.createElement("div", { className: styles.pdfListTotal }, filteredDocs?.length ?? 0))) : (React.createElement("div", { className: styles.pdfListTotal },
                React.createElement(LoadingIndicator, { style: {
                        width: "40px",
                        height: "19px",
                        transform: "scale(0.35)",
                        margin: "-8px 5px 12px -10px"
                    } })))),
            React.createElement("div", { className: styles.tBot }, filteredDocs?.length === 1 ? "Total Document" : "Total Documents")),
        isOpenDocuments && hasDocuments && (React.createElement("div", { className: styles.pdfList },
            React.createElement("div", { className: styles.pdfListTitle },
                "Documents Uploaded",
                React.createElement("div", { className: styles.pdfListItemContainer },
                    React.createElement("div", { className: styles.pdfListItem }, filteredDocs?.map((item, index) => {
                        return (React.createElement("div", { className: styles.pdfListItem, key: item + "__" + index }, item));
                    }))))))));
};
const CorpusLineage = ({ isOpenSettings, isOpenDocuments, updatedDate }) => {
    return (React.createElement("div", { className: styles.openOverview, style: {
            maxHeight: "190px",
            width: isOpenSettings || isOpenDocuments ? "100%" : "calc(33.5% - 15px)",
            overflow: "hidden",
            marginLeft: isOpenSettings || isOpenDocuments ? "10px" : "0px"
        } },
        React.createElement("div", { className: styles.senseTitle },
            React.createElement("div", { className: styles.sTitle },
                React.createElement("span", { className: styles.sIcon },
                    React.createElement("img", { src: ClockIcon, style: { height: "16px", width: "16px" } })),
                React.createElement("span", null, "Lineage"))),
        React.createElement("div", { className: styles.updateTiles },
            React.createElement("div", { className: styles.tileFlex },
                React.createElement("div", { className: styles.tTop },
                    React.createElement("span", { title: moment.utc(updatedDate).local().format("MMMM Do YYYY, h:mm:ss a") }, moment.utc(updatedDate).local().fromNow())),
                React.createElement("div", { className: styles.tBot }, "LAST UPDATE")))));
};
export const CorpusUrlModal = ({ corpusId, isOpen, updateIsOpen, updateIsLoadingCorpus }) => {
    const dispatch = useDispatch();
    const corpus = useSelector((state) => getCorpus(state, corpusId));
    const [addUrlList, updateAddUrlList] = useState([]);
    const [newLink, updateNewLink] = useState("");
    const handleAddUrl = (val) => {
        const link = val.trim();
        const currentTime = new Date().getTime();
        const newItem = {
            id: currentTime,
            url: link
        };
        if (link !== "") {
            updateAddUrlList(addUrlList.concat([newItem]));
        }
    };
    const handleUrlUpload = () => {
        if (corpus === undefined) {
            return;
        }
        const urlArray = addUrlList.map((i) => i.url);
        updateIsLoadingCorpus(true);
        dispatch(uploadDocumentUrls(corpus, urlArray))
            .then(() => {
            updateIsLoadingCorpus(false);
        })
            .catch((e) => {
            updateIsLoadingCorpus(false);
            console.log(e);
            throw e;
        });
        updateAddUrlList([]);
        updateNewLink("");
    };
    return (React.createElement("div", { id: "corpusUrlModal" },
        React.createElement(Dialog, { open: isOpen && corpus !== undefined, onOpenChange: () => updateIsOpen(false) },
            React.createElement(DialogContent, null,
                React.createElement(DialogHeader, null,
                    React.createElement(DialogTitle, null, "Add URLs to " + corpus?.name)),
                React.createElement("div", { className: styles.corpusUrlModalBodyWrapper },
                    React.createElement("div", { className: styles.corpusUrlModalTitle }, "In order for ChatAible to index a URL, the pages must:"),
                    React.createElement("div", { className: styles.corpusUrlModalList }, "1. Be static HTML"),
                    React.createElement("div", { className: styles.corpusUrlModalList }, "2. Be publicly accesible and not encrypted or password-protected."),
                    React.createElement("div", { className: styles.corpusUrlModalList }, "3. Contain readable text. Text within images won't be processed correctly."),
                    React.createElement("div", { className: styles.corpusUrlModalSubtitle }, "If you are uncertain about any of the requirements, consider saving the page as a PDF."),
                    React.createElement("div", { className: styles.corpusUrlModalLabel }, "What URLs would you like to include?"),
                    addUrlList.map((item, index) => {
                        return (React.createElement("div", { key: index, className: styles.corpusUrlInputWrapper },
                            React.createElement(LineInput, { placeholder: "Paste a webpage URL", value: item.url, key: item.id, onChange: (val) => {
                                    const newUrl = val.trim();
                                    if (newUrl === "") {
                                        updateAddUrlList(addUrlList.filter((x) => x.id != item.id));
                                    }
                                    else {
                                        updateAddUrlList(addUrlList.map((x) => (x.id == item.id ? { id: x.id, url: newUrl } : x)));
                                    }
                                }, updateInstant: false })));
                    }),
                    React.createElement("div", { className: styles.corpusUrlInputWrapper },
                        React.createElement("input", { placeholder: "Paste a webpage URL", value: newLink, onChange: (e) => {
                                const tempOld = newLink ?? "";
                                const newValue = e?.target?.value;
                                if (newValue?.length - tempOld?.length <= 1) {
                                    updateNewLink(newValue);
                                }
                                else {
                                    handleAddUrl(newValue);
                                    updateNewLink("");
                                }
                            }, onBlur: () => {
                                handleAddUrl(newLink);
                                updateNewLink("");
                            }, style: {
                                width: "100%"
                            } }))),
                React.createElement(DialogFooter, null,
                    React.createElement(Button, { type: "secondary", onClick: () => {
                            updateIsOpen(false);
                            updateAddUrlList([]);
                            updateNewLink("");
                        } }, "Cancel"),
                    React.createElement(Button, { onClick: () => {
                            handleUrlUpload();
                            updateIsOpen(false);
                        }, disabled: addUrlList.length < 1 }, "Update Document Set"))))));
};
const SenseCorpusTile = ({ corpusId, isPinned, updateIsPinned }) => {
    const dispatch = useDispatch();
    const corpus = useSelector((state) => getCorpus(state, corpusId));
    const name = corpus?.name;
    const isNotEmptyDataset = Boolean(corpus?.metadata?.document_list?.length);
    const updatedDate = corpus?.updated_date;
    const cloudAccountName = useSelector((state) => getAccountName(state, corpus?.account_id));
    const documentSetError = corpus?.metadata?.message !== undefined;
    const [isOpenSettings, updateIsOpenSettings] = useState(false);
    const [isOpenDocuments, updateIsOpenDocuments] = useState(false);
    const [isUrlModalOpen, updateIsUrlModalOpen] = useState(false);
    const [isLoadingCorpus, updateIsLoadingCorpus] = useState(false);
    const retIsLoadingCorpus = isLoadingCorpus || corpus?.metadata?.status === "INPROGRESS";
    return (React.createElement("div", { className: styles.senseTile },
        React.createElement("div", { className: styles.tileHeaderNoHover },
            React.createElement("div", { className: styles.tFlex },
                React.createElement("img", { src: documentSetIcon, style: {
                        height: "30px",
                        width: "30px",
                        marginRight: "5px",
                        background: "transparent",
                        transform: "none"
                    } }),
                React.createElement("div", { style: { width: "100%" } },
                    React.createElement("div", { style: { textTransform: "uppercase" } },
                        React.createElement("span", null, "Document Set ID: " + corpusId),
                        React.createElement("span", { style: { margin: "0px 4px" } }, "|"),
                        React.createElement("span", null, "Cloud Account: " + cloudAccountName)),
                    React.createElement("div", { className: styles.tileHeaderLarge }, name ?? "Loading..."))),
            React.createElement("div", { className: styles.headerActionBox, style: { background: "transparent" } },
                React.createElement("div", { className: styles.headerButton }),
                React.createElement("div", { className: styles.headerButton },
                    React.createElement(Button, { onClick: (e) => {
                            e.stopPropagation();
                            dispatch(deleteCorpus(corpusId));
                        }, style: { padding: "6px" }, type: "transparent" },
                        React.createElement("img", { className: styles.pinImage, src: TrashIcon }))),
                React.createElement("div", { className: styles.headerButton },
                    React.createElement(Button, { onClick: (e) => {
                            e.stopPropagation();
                            updateIsPinned(!isPinned);
                        }, style: { padding: "6px" }, active: isPinned, type: "transparent", className: styles.pinButton },
                        React.createElement("img", { className: styles.pinNoHover, src: isPinned ? PinnedIcon : UnpinnedIcon }),
                        React.createElement("img", { className: styles.pinHover, src: isPinned ? PinnedHoverIcon : UnpinnedIcon }))))),
        name !== undefined && updatedDate !== undefined && (React.createElement("div", { className: styles.tileBody },
            React.createElement("div", { className: styles.senseSection },
                React.createElement("div", { className: styles.senseAll },
                    React.createElement(CorpusAction, { corpusId: corpusId, openCorpusUrlModal: () => updateIsUrlModalOpen(true), isLoadingCorpus: retIsLoadingCorpus, updateIsLoadingCorpus: updateIsLoadingCorpus, isNotEmptyDataset: isNotEmptyDataset }),
                    documentSetError && React.createElement(SenseCorpusErrorCard, { currentCorpus: corpus }),
                    React.createElement(CorpusDocumentList, { corpusId: corpusId, isOpenDocuments: isOpenDocuments, isOpenSettings: isOpenSettings, updateIsOpenDocuments: updateIsOpenDocuments, isLoadingCorpus: retIsLoadingCorpus }),
                    React.createElement(CorpusSettings, { corpusId: corpusId, isOpenSettings: isOpenSettings, isOpenDocuments: isOpenDocuments, toggleIsOpen: () => updateIsOpenSettings(!isOpenSettings), isLoadingCorpus: retIsLoadingCorpus, updateIsLoadingCorpus: updateIsLoadingCorpus }),
                    React.createElement(CorpusLineage, { isOpenSettings: isOpenSettings, isOpenDocuments: isOpenDocuments, updatedDate: updatedDate }),
                    React.createElement(CorpusUrlModal, { corpusId: corpusId, isOpen: isUrlModalOpen, updateIsOpen: updateIsUrlModalOpen, updateIsLoadingCorpus: updateIsLoadingCorpus })))))));
};
export default SenseCorpusTile;
